/** tm/components/AIBizplanConfigPanel.tsx — AI 사업계획서 설정 패널 (row 형태 파일 첨부) */
import { useState, useRef } from 'react';
import type { AIBizplanConfig, AIPreset } from '../types';
import { C, T, R } from './ui';

interface Props {
  value: AIBizplanConfig;
  onChange: (v: AIBizplanConfig) => void;
  presets: AIPreset[];
}

const lbl: React.CSSProperties = { fontSize: T.xs, fontWeight: 600, color: C.sub, display: 'block', marginBottom: 4 };
const inp: React.CSSProperties = { width: '100%', padding: '7px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, outline: 'none', boxSizing: 'border-box' as const };

const PHASES: { key: AIBizplanConfig['phase']; label: string; num: string }[] = [
  { key: 'settings', label: '설정', num: '①' },
  { key: 'generating', label: 'AI생성대기', num: '②' },
  { key: 'reviewing', label: '결과확인', num: '③' },
];

const DEFAULT_LABELS = ['사업계획서 양식', '작성 가이드', '선정 기준표', '샘플 사업계획서', '기타'];

export default function AIBizplanConfigPanel({ value, onChange, presets }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetLabel = useRef('');
  const [savingPreset, setSavingPreset] = useState(false);
  const [includeCollect, setIncludeCollect] = useState(!!(value as unknown as Record<string,unknown>).includeCollectFiles);
  const [presetName, setPresetName] = useState('');
  const [showAddLabel, setShowAddLabel] = useState(false);
  const [newDocLabel, setNewDocLabel] = useState('');

  const filtered = presets.filter(p => p.type === 'ai_bizplan');
  const files = value.files || [];

  // 파일 라벨 기반 동적 slots (프리셋 선택 시 프리셋 라벨, 없으면 DEFAULT)
  const fileLabels = files.length > 0 ? files.map(f => f.label) : DEFAULT_LABELS;
  const slots: { label: string; removable: boolean; hint?: string }[] =
    fileLabels.map(label => ({
      label,
      removable: !DEFAULT_LABELS.includes(label),
      hint: label === '사업계획서 양식' ? 'AI가 이 양식의 각 항목을 채워줍니다' : undefined,
    }));

  function getFile(label: string) { return files.find(f => f.label === label); }

  function handlePreset(id: string) {
    const preset = filtered.find(p => p.id === id);
    if (!preset) { onChange({ ...value, presetId: '' }); return; }
    const raw = preset as unknown as Record<string, unknown>;
    const labels = Array.isArray(raw.doc_labels) ? raw.doc_labels as string[] : [];
    const pFiles = Array.isArray(raw.files) ? raw.files as { label: string; name: string; s3_key: string }[] : [];
    const nextFiles = labels.map(lb => {
      const match = pFiles.find(f => f.label === lb);
      return match ? { label: lb, name: match.name, s3_key: match.s3_key } : { label: lb, name: '', s3_key: '' };
    });
    for (const pf of pFiles) { if (!nextFiles.find(nf => nf.label === pf.label)) { nextFiles.push({ label: pf.label, name: pf.name, s3_key: pf.s3_key }); } }
const incCollect = !!(raw.include_collect_files);
    setIncludeCollect(incCollect);
    onChange({ ...value, presetId: id, files: nextFiles, extraPrompt: preset.prompt || value.extraPrompt, includeCollectFiles: incCollect } as typeof value);
  }

  function triggerUpload(label: string) {
    uploadTargetLabel.current = label;
    fileInputRef.current?.click();
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const targetLabel = uploadTargetLabel.current;
    const token = localStorage.getItem('token') || '';
    const fd = new FormData();
    fd.append('file', f);
    fetch('/api/tm/upload-file', { method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd })
      .then(r => r.json())
      .then(json => {
        if (json.data) {
          const newFile = { label: targetLabel, name: json.data.name || f.name, s3_key: json.data.s3_key };
          const idx = files.findIndex(ff => ff.label === targetLabel);
          if (idx >= 0) {
            const next = [...files];
            next[idx] = newFile;
            onChange({ ...value, files: next });
          } else {
            onChange({ ...value, files: [...files, newFile] });
          }
        }
      })
      .catch(() => {});
    e.target.value = '';
  }

  function removeFile(label: string) {
    onChange({ ...value, files: files.filter(f => f.label !== label) });
  }

  function removeExtraSlot(label: string) {
    onChange({ ...value, files: files.filter(f => f.label !== label) });
  }

  async function handleSavePreset() {
    if (!presetName.trim()) return;
    const token = localStorage.getItem('token') || '';
    try {
      await fetch('/api/tm/ai-presets', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: presetName.trim(),
          type: 'ai_bizplan',
          docs: files.map(f => ({ name: f.name, s3_key: f.s3_key })),
          prompt: value.extraPrompt || '',
        }),
      });
      setSavingPreset(false);
      setPresetName('');
    } catch { /* ignore */ }
  }

  const rowS: React.CSSProperties = {
    display: 'flex', gap: 8, alignItems: 'center', padding: 8,
    borderRadius: R.sm, marginBottom: 4,
  };
  const attachS: React.CSSProperties = {
    padding: '4px 10px', border: `1px solid ${C.accent}`, color: C.accent,
    background: C.white, borderRadius: R.sm, fontSize: T.xs, cursor: 'pointer',
    fontWeight: 600, whiteSpace: 'nowrap' as const, flexShrink: 0,
  };

  return (
    <div>
      {/* 단계 진행상태 배지 */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {PHASES.map(p => {
          const active = p.key === value.phase;
          const done = PHASES.findIndex(x => x.key === value.phase) > PHASES.findIndex(x => x.key === p.key);
          return (
            <div key={p.key} style={{
              flex: 1, textAlign: 'center', padding: '6px 0', borderRadius: R.sm, fontSize: T.xs, fontWeight: 600,
              background: active ? C.accentLt : done ? C.greenLt : C.bg,
              color: active ? C.accent : done ? C.green : C.muted,
              border: `1px solid ${active ? C.accent : done ? C.green : C.border}`,
            }}>
              {p.num} {p.label}
            </div>
          );
        })}
      </div>

      {/* AI 프리셋 선택 */}
      <div style={lbl}>AI 프리셋 (선택사항)</div>
      <select value={value.presetId || ''} onChange={e => handlePreset(e.target.value)}
        style={{ ...inp, marginBottom: 12 }}>
        <option value="">프리셋 없이 직접 설정</option>
        {filtered.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>

      {/* AI에게 전달할 파일 */}
      <div style={lbl}>AI에게 전달할 파일</div>
      {/* 1번: 앞단계 자료수집 파일 자동 전달 토글 */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 12px', borderRadius: R.sm, marginBottom: 6,
        border: `1px solid ${includeCollect ? '#7C3AED' : C.border}`,
        background: includeCollect ? '#F5F3FF' : C.white,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: T.xs, color: '#7C3AED', fontWeight: 600, width: 16, textAlign: 'right', flexShrink: 0 }}>①</span>
          <span style={{ fontSize: T.xs, fontWeight: 600, color: C.text }}>앞단계 자료수집 파일 자동 전달</span>
        </div>
        <div onClick={() => { const next = !includeCollect; setIncludeCollect(next); onChange({...value, includeCollectFiles: next} as typeof value); }}
          style={{ width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative', transition: 'all 0.2s',
            background: includeCollect ? '#7C3AED' : '#D1D5DB' }}>
          <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2,
            left: includeCollect ? 22 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
        </div>
      </div>
      {slots.map((slot, si) => {
        const file = getFile(slot.label);
        return (
          <div key={slot.label + si}>
            <div style={{...rowS, border: `1px solid ${file ? C.purple : C.border}`, background: file ? C.purpleLt : C.white }}>
              <span style={{ fontSize: T.xs, color: C.muted, flexShrink: 0, width: 16, textAlign: 'right' }}>{si + 2}.</span>
              <span style={{ fontSize: T.xs, fontWeight: 600, color: C.text, minWidth: 100, flexShrink: 0 }}>{slot.label}</span>
              <span style={{ flex: 1, fontSize: T.xs, color: file ? C.accent : C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {file ? file.name : '미첨부'}
              </span>
              <button onClick={() => triggerUpload(slot.label)} style={attachS}>
                {file ? '변경' : '첨부하기'}
              </button>
              {file && (
                <button onClick={() => removeFile(slot.label)}
                  style={{ background: 'none', border: 'none', color: C.red, fontSize: T.xs, cursor: 'pointer', padding: '2px 4px', flexShrink: 0 }}>
                  ✕
                </button>
              )}
              {!file && slot.removable && (
                <button onClick={() => removeExtraSlot(slot.label)}
                  style={{ background: 'none', border: 'none', color: C.muted, fontSize: T.xs, cursor: 'pointer', padding: '2px 4px', flexShrink: 0 }}>
                  ✕
                </button>
              )}
            </div>
            {slot.hint && (
              <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 4, paddingLeft: 28 }}>{slot.hint}</div>
            )}
          </div>
        );
      })}
      {showAddLabel ? (
        <div style={{ display: 'flex', gap: 4, marginTop: 4, marginBottom: 12 }}>
          <input value={newDocLabel} onChange={e => setNewDocLabel(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && newDocLabel.trim()) { onChange({ ...value, files: [...files, { label: newDocLabel.trim(), name: '', s3_key: '' }] }); setNewDocLabel(''); setShowAddLabel(false); } }}
            placeholder="항목명 입력" autoFocus
            style={{ flex: 1, padding: '6px 10px', borderRadius: R.sm, border: `1px solid ${C.accent}`, fontSize: T.xs, outline: 'none' }} />
          <button onClick={() => { if (newDocLabel.trim()) { onChange({ ...value, files: [...files, { label: newDocLabel.trim(), name: '', s3_key: '' }] }); setNewDocLabel(''); setShowAddLabel(false); } }}
            style={{ padding: '6px 12px', borderRadius: R.sm, border: 'none', background: C.accent, color: '#fff', fontSize: T.xs, fontWeight: 700, cursor: 'pointer' }}>추가</button>
          <button onClick={() => { setShowAddLabel(false); setNewDocLabel(''); }}
            style={{ padding: '6px 8px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: '#fff', color: C.sub, fontSize: T.xs, cursor: 'pointer' }}>취소</button>
        </div>
      ) : (
        <button onClick={() => setShowAddLabel(true)}
          style={{ padding: '6px 14px', borderRadius: R.sm, border: `1px dashed ${C.border}`, background: C.white, color: C.accent, fontSize: T.xs, fontWeight: 600, cursor: 'pointer', marginTop: 4, marginBottom: 12 }}>
          + 항목 추가
        </button>
      )}

      {/* 추가 지시사항 */}
      <div style={lbl}>추가 지시사항</div>
      <textarea value={value.extraPrompt || ''} onChange={e => onChange({ ...value, extraPrompt: e.target.value })}
        placeholder="AI에게 전달할 추가 지시사항을 입력하세요..."
        rows={3} style={{ ...inp, resize: 'vertical', fontFamily: 'inherit', marginBottom: 8 }} />

      {/* 프리셋으로 저장 */}
      {!savingPreset ? (
        <button onClick={() => setSavingPreset(true)}
          style={{ fontSize: T.xs, color: C.muted, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0' }}>
          💾 현재 설정을 프리셋으로 저장
        </button>
      ) : (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
          <input value={presetName} onChange={e => setPresetName(e.target.value)} placeholder="프리셋 이름"
            onKeyDown={e => { if (e.key === 'Enter') handleSavePreset(); }}
            style={{ ...inp, flex: 1 }} />
          <button onClick={handleSavePreset}
            style={{ padding: '6px 14px', borderRadius: R.sm, border: 'none', background: C.accent, color: '#fff', fontSize: T.xs, fontWeight: 600, cursor: 'pointer' }}>저장</button>
          <button onClick={() => { setSavingPreset(false); setPresetName(''); }}
            style={{ padding: '6px 10px', borderRadius: R.sm, border: `1px solid ${C.border}`, background: C.white, color: C.muted, fontSize: T.xs, cursor: 'pointer' }}>취소</button>
        </div>
      )}

      <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={onFileChange} />
    </div>
  );
}
